// i18n provider — resolves the locale (config.js), serves t() and the
// formatters (format.js), persists the choice, and keeps <html lang/dir> in
// sync. The inline script in index.html owns only the pre-paint frame; from
// mount onward this owns it, and nothing else in the app sets `direction`.

import React from 'react';
import { SUPPORTED, STORAGE_KEY, resolveLocale } from './config.js';
import { catalogs, en } from './catalogs.js';
import { createFormatters } from './format.js';
import { createTranslator } from './translator.js';

const I18nContext = React.createContext(null);

// Direction-aware icon transform. `--dir-flip` is +1 in RTL and -1 in LTR
// (styles.css), so this mirrors a glyph whose MEANING is directional — a back
// arrow, a next-day chevron. It is opt-in per icon: a magnifier or a phone
// handset mirrored by accident is the classic RTL bug.
export const MIRROR_ICON = { transform: 'scaleX(var(--dir-flip, 1))' };

// Digit/ASCII runs that must read left-to-right inside either layout: phone
// numbers, national ids, medical codes. `isolate` is what stops adjacent
// punctuation being dragged to the wrong end.
export const LTR_TEXT = { dir: 'ltr', style: { unicodeBidi: 'isolate' } };

/**
 * `dir` for a free-text field whose content may be either script.
 *
 * `dir="auto"` alone is WRONG ON AN EMPTY FIELD. For an input or textarea the
 * spec resolves `auto` against the element's VALUE, not its placeholder: with
 * no strong directional character to find it falls back to **ltr**, so a
 * Persian placeholder sat left-aligned with its «…» at the wrong end until the
 * dentist typed the first letter.
 *
 * So: inherit the document direction while the field is empty (the placeholder
 * is written in the UI language, and `<html dir>` is already that language),
 * and hand over to `auto` once there is content — which is the part that was
 * actually wanted, so a pasted English drug name still reads left-to-right
 * inside an RTL form.
 */
export const autoDir = (value) => (value ? 'auto' : undefined);

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = React.useState(() =>
    resolveLocale(
      typeof localStorage !== 'undefined' ? localStorage : null,
      typeof navigator !== 'undefined'
        ? (navigator.languages?.length ? navigator.languages : [navigator.language])
        : [],
    ),
  );

  React.useEffect(() => {
    document.documentElement.setAttribute('lang', locale);
    document.documentElement.setAttribute('dir', SUPPORTED[locale]);
  }, [locale]);

  const setLocale = React.useCallback((next) => {
    if (!SUPPORTED[next]) return;
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* storage off */
    }
    setLocaleState(next);
  }, []);

  const value = React.useMemo(() => {
    const formatters = createFormatters(locale);
    return {
      locale,
      dir: SUPPORTED[locale],
      isRTL: SUPPORTED[locale] === 'rtl',
      setLocale,
      t: createTranslator({
        catalogs, fallback: en, locale, formatNumber: formatters.formatNumber,
      }),
      ...formatters,
    };
  }, [locale, setLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

// A fallback value for anything rendered outside the provider (a test, an error
// boundary that mounted above it). It reports English and formats with English
// rules rather than throwing: "no translations" must never be the reason a
// clinical screen fails to render.
let orphan = null;
function orphanValue() {
  if (!orphan) {
    const formatters = createFormatters('en');
    orphan = {
      locale: 'en',
      dir: 'ltr',
      isRTL: false,
      setLocale: () => {},
      t: createTranslator({
        catalogs, fallback: en, locale: 'en', formatNumber: formatters.formatNumber,
      }),
      ...formatters,
    };
  }
  return orphan;
}

export function useTranslation() {
  return React.useContext(I18nContext) ?? orphanValue();
}
